/**
 * What a node card needs to know that is not part of the flow.
 *
 * It travels by context rather than in `node.data` on purpose: `data` is SAVED, and a
 * compiler error or a hover state that ends up in the database is a bug that survives a
 * restart. Everything here is render-only and dies with the screen.
 */
import { createContext, useContext } from 'react';

export type FlowUi = {
  /** Errors from the compiler, keyed by node id. A card renders its own. */
  issues: Map<string, string[]>;
  /** Non-fatal notes, same shape. */
  warnings: Map<string, string[]>;
  /** Situation key → the words an owner uses for it. */
  situationLabel: (key: string) => string;
  /** True when the role cannot write, or the flow is locked pending a decision. */
  readOnly: boolean;
  /** Select this node and move focus into the inspector — the keyboard route to editing. */
  onEdit: (id: string) => void;
  /** What starts this flow, shown on the entry card so it is not an anonymous circle. */
  trigger: string;
};

const FALLBACK: FlowUi = {
  issues: new Map(),
  warnings: new Map(),
  situationLabel: (k) => k,
  readOnly: true,
  onEdit: () => {},
  trigger: '',
};

export const FlowUiContext = createContext<FlowUi>(FALLBACK);

export function useFlowUi(): FlowUi {
  return useContext(FlowUiContext);
}
